import { ReactNode, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, CheckCircle2, Loader2, MoreVertical, Pencil, Send } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Modal } from '@/components/ui/modal';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { UserAvatar } from '@/components/ui/user-avatar';
import { useI18n } from '@/hooks/useI18n';
import { IInstallment } from '@/lib/builder/models/payment-plan.model';
import { MarkInstallmentPaidModal } from '@/lib/builder/components/payment-plan/MarkInstallmentPaidModal';
import { installmentBadgeClassName } from '@/lib/builder/components/payment-plan/InstallmentStatusBadge';
import { formatPlanAmount, formatPlanDate } from '@/lib/builder/utils/payment-format';
import {
    DUE_WINDOW_ALL,
    IDueInstallment,
    PeopleDictionary,
    personOf,
} from '@/lib/payments/models/payments-dashboard.model';
import { payerKey } from '@/lib/payments/utils/traveler-copy';

interface DueInstallmentsCardProps {
    rows: IDueInstallment[];
    /** Anagrafiche citate dalle righe: le righe portano solo la chiave. */
    people: PeopleDictionary;
    dueWindowDays: number;
    isLoading: boolean;
    /** TD, agenzia e admin gestiscono le rate (modifica piano, solleciti); il viaggiatore paga. */
    canManage: boolean;
    /**
     * Registrare un incasso a mano e' riservato all'admin: e' una scrittura contabile che
     * nessuno verifica contro Stripe, e chi vende il viaggio non deve poter dichiarare di
     * aver incassato. Il guard vero sta in `mark_installment_paid`; qui si nasconde solo
     * cio' che verrebbe rifiutato.
     */
    canMarkPaid: boolean;
    canPay: boolean;
    isMarkingPaid: boolean;
    /**
     * Rata per cui si sta aprendo il checkout, se ce n'e' una. Serve l'id e non un
     * booleano: il pulsante in attesa deve essere quello cliccato, non tutti quelli a
     * schermo. Finche' e' valorizzato gli altri "Paga" restano disabilitati, perche' due
     * checkout aperti sulla stessa rata si contenderebbero la sessione Stripe.
     */
    payingInstallmentId: string | null;
    isSendingReminder: boolean;
    /** Rate gia' sollecitate in questa sessione: il database rifiuta comunque un secondo
     *  invio entro un'ora, ma il pulsante deve dirlo prima del click. */
    remindedIds: Set<string>;
    onSendReminder: (params: { installmentId: string }) => void;
    onMarkPaid: (params: {
        installmentId: string;
        itineraryId: string;
        paymentMethod: string;
    }) => void;
    onPay: (params: { installmentId: string; itineraryId: string }) => void;
    /**
     * Apre l'editor del piano a cui la rata appartiene, con tutte le altre rate, e con
     * questa evidenziata e a fuoco.
     */
    onEditPlan: (itineraryId: string, installmentId: string) => void;
    /** Nascosta per TD e viaggiatore: sarebbe una colonna con sempre lo stesso nome. */
    showDesigner: boolean;
    /**
     * Il viaggiatore paga: le rate sono "da pagare", non "da incassare". Nasconde anche
     * il nome cliente sotto il titolo del viaggio, che per lui e' sempre il proprio.
     */
    travelerView?: boolean;
    /** Impaginazione, montata sotto le righe: la card non sa quante pagine esistono. */
    footer?: ReactNode;
}

/**
 * Adatta una riga della dashboard alla forma che la modale di conferma si aspetta.
 *
 * La modale nasce nella card del builder e legge solo label, scadenza e importo; il resto
 * di IInstallment non le serve. Riusarla e' obbligatorio: e' l'unico punto in cui l'utente
 * viene avvisato che il primo incasso manda l'itinerario a completato e fa scadere le
 * proposte alternative.
 */
const toModalInstallment = (row: IDueInstallment): IInstallment =>
    ({
        id: row.installment_id,
        label: row.label ?? '',
        amount: row.amount,
        due_date: row.due_date,
        seq: row.seq,
        status: 'pending',
        is_overdue: row.is_overdue,
        payment_processing: row.payment_processing,
    }) as IInstallment;

/**
 * Rate da incassare, scadute in cima.
 *
 * Scadute e in scadenza stanno nella stessa tabella di proposito: sono la stessa coda di
 * lavoro, e in due liste separate si finirebbe per guardarne una sola. La distinzione la
 * porta il badge, non la posizione nella pagina.
 */
export const DueInstallmentsCard = ({
    rows,
    people,
    dueWindowDays,
    isLoading,
    canManage,
    canMarkPaid,
    canPay,
    isMarkingPaid,
    payingInstallmentId,
    isSendingReminder,
    remindedIds,
    onSendReminder,
    onMarkPaid,
    onPay,
    onEditPlan,
    showDesigner,
    travelerView,
    footer,
}: DueInstallmentsCardProps) => {
    const { t } = useI18n();
    const navigate = useNavigate();
    const [rowToConfirm, setRowToConfirm] = useState<IDueInstallment | null>(null);
    /**
     * Rata per cui si sta chiedendo conferma del sollecito. Il click sulla voce di menu
     * non invia piu' nulla: l'email parte solo dal pulsante della modale, perche' un
     * sollecito partito per sbaglio arriva al cliente e non si annulla.
     */
    const [rowToRemind, setRowToRemind] = useState<IDueInstallment | null>(null);

    /**
     * Il pulsante "Paga" sta su una rata sola per viaggio: la prima aperta. Le scadenze
     * di un piano sono crescenti per validazione, quindi la prima per data e' anche la
     * prima per numero. Due CTA di pagamento sullo stesso viaggio si contenderebbero il
     * click e Stripe accetterebbe comunque solo la rata che la edge function rilegge.
     */
    const payableIds = useMemo(() => {
        const seen = new Set<string>();
        const payable = new Set<string>();
        rows.forEach((row) => {
            if (seen.has(row.itinerary_id)) return;
            seen.add(row.itinerary_id);
            payable.add(row.installment_id);
        });
        return payable;
    }, [rows]);

    const dueLabel = (row: IDueInstallment) => {
        if (row.payment_processing) return t('payments.due.processing');
        if (row.is_overdue) {
            return t('payments.due.overdueBy', { days: Math.abs(row.days_to_due) });
        }
        if (row.days_to_due === 0) return t('payments.due.today');
        return t('payments.due.inDays', { days: row.days_to_due });
    };

    return (
        <>
            <Card>
                <CardHeader className="p-4 pb-3 flex-row items-center justify-between space-y-0">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                        <CalendarClock className="w-4 h-4 text-gray-500" />
                        {t('payments.due.title')}
                    </CardTitle>
                    <span className="text-xs text-gray-500">
                        {dueWindowDays === DUE_WINDOW_ALL
                            ? t(payerKey('payments.due.windowHintAll', travelerView))
                            : t('payments.due.windowHint', { days: dueWindowDays })}
                    </span>
                </CardHeader>

                <CardContent className="p-0">
                    {rows.length === 0 ? (
                        <p className="px-4 pb-4 text-sm text-gray-500">
                            {isLoading
                                ? t('common.loading')
                                : t(payerKey('payments.due.empty', travelerView))}
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t('payments.columns.trip')}</TableHead>
                                        <TableHead>{t('payments.columns.installment')}</TableHead>
                                        {showDesigner && (
                                            <TableHead>{t('payments.columns.designer')}</TableHead>
                                        )}
                                        <TableHead>{t('payments.columns.dueDate')}</TableHead>
                                        <TableHead className="text-right">
                                            {t('payments.columns.amount')}
                                        </TableHead>
                                        <TableHead className="w-[1%]" />
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {rows.map((row) => (
                                        <TableRow
                                            key={row.installment_id}
                                            className={row.is_overdue ? 'bg-red-50/60' : undefined}
                                        >
                                            <TableCell className="align-top">
                                                <button
                                                    type="button"
                                                    className="font-medium text-gray-900 hover:underline text-left"
                                                    onClick={() =>
                                                        navigate(
                                                            `/itinerary-builder?itineraryId=${row.itinerary_id}`
                                                        )
                                                    }
                                                >
                                                    {row.itinerary_title}
                                                </button>
                                                {/* Per il viaggiatore sarebbe sempre il
                                                    proprio nome, su ogni riga. */}
                                                {!travelerView && (
                                                    <span className="block text-xs text-gray-500">
                                                        {personOf(people, row.client_key)?.name ?? '—'}
                                                    </span>
                                                )}
                                            </TableCell>

                                            {/* Si apre il piano intero, non la singola
                                                rata: gli importi devono quadrare col
                                                totale, e una riga modificata da sola
                                                sfonderebbe la quadratura senza mostrare
                                                dove. */}
                                            <TableCell className="align-top">
                                                {canManage ? (
                                                    <button
                                                        type="button"
                                                        className="text-gray-900 hover:underline text-left"
                                                        title={t('payments.actions.editPlan')}
                                                        onClick={() =>
                                                            onEditPlan(
                                                                row.itinerary_id,
                                                                row.installment_id
                                                            )
                                                        }
                                                    >
                                                        {row.label ?? '—'}
                                                    </button>
                                                ) : (
                                                    <span className="text-gray-900">
                                                        {row.label ?? '—'}
                                                    </span>
                                                )}
                                                {/* "1 di 1" e' il pagamento unico: si dice
                                                    com'e', non contando. */}
                                                <span className="block text-xs text-gray-500">
                                                    {row.installments_count === 1
                                                        ? t('payments.due.singlePayment')
                                                        : t('payments.due.seqOf', {
                                                              seq: row.seq,
                                                              total: row.installments_count,
                                                          })}
                                                </span>
                                            </TableCell>

                                            {/* Nome per esteso accanto al pallino: le sole
                                                iniziali costringevano a passare il mouse
                                                riga per riga per sapere di chi fosse la
                                                rata. Lo spazio arriva dalla colonna azioni,
                                                ora raccolta in un menu. */}
                                            {showDesigner && (
                                                <TableCell className="align-top text-sm text-gray-600">
                                                    {(() => {
                                                        const designer = personOf(people, row.designer_id);
                                                        if (!designer) return '—';
                                                        return (
                                                            <UserAvatar
                                                                expanded
                                                                fullName={designer.name}
                                                                email={designer.email}
                                                                imageUrl={designer.avatar_url}
                                                                size="sm"
                                                                showTooltip
                                                            />
                                                        );
                                                    })()}
                                                </TableCell>
                                            )}

                                            <TableCell className="align-top">
                                                <span className="text-gray-900">
                                                    {formatPlanDate(row.due_date)}
                                                </span>
                                                <span className="block mt-0.5">
                                                    {/* Colori dal badge delle rate: qui
                                                        cambia solo il testo ("Scaduta da 3
                                                        gg" invece di "Scaduta"), non il
                                                        significato dello stato. */}
                                                    <Badge
                                                        className={installmentBadgeClassName({
                                                            status: 'pending',
                                                            isOverdue: row.is_overdue,
                                                            isProcessing: row.payment_processing,
                                                        })}
                                                    >
                                                        {dueLabel(row)}
                                                    </Badge>
                                                </span>
                                            </TableCell>

                                            <TableCell className="align-top text-right font-semibold text-gray-900 tabular-nums">
                                                {formatPlanAmount(row.amount)}
                                            </TableCell>

                                            <TableCell className="align-top text-right whitespace-nowrap">
                                              <div className="flex items-center justify-end gap-1">
                                                {/* Tre azioni di gestione in un menu: da
                                                    bottoni affiancati occupavano piu'
                                                    larghezza dei dati della riga, e sono
                                                    tutte azioni occasionali. */}
                                                {canManage && (
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-8 w-8 p-0"
                                                                aria-label={t('common.actions')}
                                                            >
                                                                <MoreVertical className="h-4 w-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            {/* Modifica importo, scadenza o
                                                                promemoria della rata. Si apre
                                                                comunque il piano intero, con
                                                                questa riga a fuoco: gli importi
                                                                devono quadrare col totale. */}
                                                            <DropdownMenuItem
                                                                onClick={() =>
                                                                    onEditPlan(
                                                                        row.itinerary_id,
                                                                        row.installment_id
                                                                    )
                                                                }
                                                            >
                                                                <Pencil className="mr-2 h-4 w-4" />
                                                                {t('payments.actions.editPlan')}
                                                            </DropdownMenuItem>
                                                            {/* Sollecito al cliente. Sulle rate
                                                                scadute e' l'unico invio
                                                                possibile: i promemoria
                                                                pianificati hanno tutti offset
                                                                negativi e dopo la scadenza non
                                                                parte piu' nulla da solo. */}
                                                            <DropdownMenuItem
                                                                disabled={
                                                                    isSendingReminder ||
                                                                    remindedIds.has(row.installment_id) ||
                                                                    row.payment_processing
                                                                }
                                                                title={
                                                                    row.payment_processing
                                                                        ? t('payments.actions.processingHint')
                                                                        : row.is_overdue
                                                                        ? t('payments.actions.remindOverdue')
                                                                        : t('payments.actions.remind')
                                                                }
                                                                onClick={() => setRowToRemind(row)}
                                                            >
                                                                <Send className="mr-2 h-4 w-4" />
                                                                {remindedIds.has(row.installment_id)
                                                                    ? t('payments.actions.reminded')
                                                                    : t('payments.actions.remind')}
                                                            </DropdownMenuItem>
                                                            {canMarkPaid && (
                                                            <DropdownMenuItem
                                                                // Registrarla a mano mentre
                                                                // l'addebito e' in volo
                                                                // significa incassare due volte.
                                                                disabled={isMarkingPaid || row.payment_processing}
                                                                title={
                                                                    row.payment_processing
                                                                        ? t('payments.actions.processingHint')
                                                                        : undefined
                                                                }
                                                                onClick={() => setRowToConfirm(row)}
                                                            >
                                                                <CheckCircle2 className="mr-2 h-4 w-4" />
                                                                {t('payments.actions.markPaid')}
                                                            </DropdownMenuItem>
                                                            )}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                )}
                                                {/* Il tentativo aperto non toglie il pulsante: chi ha
                                                    aperto il checkout e non ha concluso deve poter
                                                    ripartire. */}
                                                {canPay && payableIds.has(row.installment_id) && (
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        disabled={payingInstallmentId !== null}
                                                        aria-busy={
                                                            payingInstallmentId === row.installment_id
                                                        }
                                                        onClick={() =>
                                                            onPay({
                                                                installmentId: row.installment_id,
                                                                itineraryId: row.itinerary_id,
                                                            })
                                                        }
                                                    >
                                                        {payingInstallmentId === row.installment_id ? (
                                                            <>
                                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                                {t('payments.actions.payLoading')}
                                                            </>
                                                        ) : (
                                                            t('payments.actions.pay')
                                                        )}
                                                    </Button>
                                                )}
                                              </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                    {footer}
                </CardContent>
            </Card>

            {/* Conferma del sollecito: la modale del progetto, non un AlertDialog, come
                per l'incasso manuale qui accanto. */}
            <Modal
                isOpen={Boolean(rowToRemind)}
                onClose={() => setRowToRemind(null)}
                size="sm"
                title={t('payments.actions.remindConfirmTitle')}
                footer={
                    <>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setRowToRemind(null)}
                            disabled={isSendingReminder}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            type="button"
                            disabled={isSendingReminder}
                            onClick={() => {
                                if (!rowToRemind) return;
                                onSendReminder({ installmentId: rowToRemind.installment_id });
                                setRowToRemind(null);
                            }}
                        >
                            {t('payments.actions.remindConfirmCta')}
                        </Button>
                    </>
                }
            >
                <div className="flex flex-col gap-4 px-6 py-4">
                    <p className="text-sm text-gray-700">
                        {t('payments.actions.remindConfirmBody')}
                    </p>
                    {rowToRemind && (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex flex-col gap-1">
                            <span className="font-medium text-gray-900">
                                {rowToRemind.itinerary_title}
                            </span>
                            <span className="text-sm text-gray-500">
                                {t('payments.actions.remindConfirmDetail', {
                                    label: rowToRemind.label ?? '—',
                                    amount: formatPlanAmount(rowToRemind.amount),
                                    date: formatPlanDate(rowToRemind.due_date),
                                })}
                            </span>
                        </div>
                    )}
                </div>
            </Modal>

            {/* Montata solo per l'admin: la voce di menu che la apre non esiste per gli
                altri ruoli, e la RPC li rifiuterebbe comunque. */}
            {canMarkPaid && (
            <MarkInstallmentPaidModal
                installment={rowToConfirm ? toModalInstallment(rowToConfirm) : null}
                isOpen={Boolean(rowToConfirm)}
                isSaving={isMarkingPaid}
                formatAmount={formatPlanAmount}
                formatDate={formatPlanDate}
                onClose={() => setRowToConfirm(null)}
                onConfirm={({ installmentId, paymentMethod }) => {
                    if (!rowToConfirm) return;
                    onMarkPaid({
                        installmentId,
                        itineraryId: rowToConfirm.itinerary_id,
                        paymentMethod,
                    });
                    setRowToConfirm(null);
                }}
            />
            )}
        </>
    );
};
